// Client wrapper around the popularity edge functions.
// - Reads are cached in sessionStorage for 5 minutes.
// - Writes are fire-and-forget, capped per session per kind, and never
//   block UI even when the network is offline.

import { supabase } from "@/integrations/supabase/client";
import { getVisitorHash } from "@/lib/visitorId";

const POP_CACHE_KEY = "cl_pop_v1";
const POP_TTL_MS = 5 * 60 * 1000;
const SESSION_KIND_CAP = 5; // max writes per (kind, slug) per session

type CacheShape = { fetchedAt: number; popularity: Record<string, number> };

const readCache = (): CacheShape | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(POP_CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CacheShape;
  } catch {
    return null;
  }
};

const writeCache = (c: CacheShape) => {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(POP_CACHE_KEY, JSON.stringify(c));
  } catch {
    /* ignore */
  }
};

export const fetchGlobalPopularity = async (): Promise<Record<string, number>> => {
  const cached = readCache();
  if (cached && Date.now() - cached.fetchedAt < POP_TTL_MS) {
    return cached.popularity;
  }
  try {
    const { data, error } = await supabase.functions.invoke("get-project-popularity", {
      method: "GET",
    });
    if (error || !data || typeof data !== "object") return cached?.popularity ?? {};
    const popularity = (data as { popularity?: Record<string, number> }).popularity ?? {};
    writeCache({ fetchedAt: Date.now(), popularity });
    return popularity;
  } catch {
    return cached?.popularity ?? {};
  }
};

export type GlobalSignalKind =
  | "project_view"
  | "project_dwell"
  | "filter_match"
  | "hero_click"
  | "hero_hover"
  | "repeat_project"
  | "repeat_sector";

const sessionCounts = new Map<string, number>();

const cap = (kind: GlobalSignalKind, slug: string) => {
  const key = `${kind}:${slug}`;
  const n = sessionCounts.get(key) ?? 0;
  if (n >= SESSION_KIND_CAP) return false;
  sessionCounts.set(key, n + 1);
  return true;
};

export const recordGlobalEngagement = async (
  kind: GlobalSignalKind,
  payload: { slug: string; sector?: string; service?: string; points?: number },
): Promise<void> => {
  if (!payload.slug) return;
  if (!cap(kind, payload.slug)) return;
  try {
    const visitor_hash = await getVisitorHash();
    await supabase.functions.invoke("record-engagement", {
      method: "POST",
      body: {
        slug: payload.slug,
        sector: payload.sector,
        service: payload.service,
        signal_kind: kind,
        points: payload.points,
        visitor_hash,
      },
    });
  } catch {
    /* offline / blocked — silently ignore */
  }
};
